import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faRankingStar,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import {
  getFirestore,
  doc,
  onSnapshot,
  collection,
  query,
} from "firebase/firestore";

export const Results = () => {
  const [results, setResults] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [eventName, setEventName] = useState("");
  const [eventStatus, setEventStatus] = useState("completed");
  const [bibNumberInput, setBibNumberInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const debounceRef = useRef(null);
  const canvasRef = useRef(null);
  const db = getFirestore();
  const selectedEventId = "verticalchallenge";

  // Real-time listener for event data
  useEffect(() => {
    const eventDocRef = doc(db, "timingEvents", selectedEventId);

    const unsubscribeEvent = onSnapshot(
      eventDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setEventName(data.eventName || "Vertical Challenge");
          setEventStatus(data.status || "completed");
          setRounds(
            data.rounds && Array.isArray(data.rounds) ? data.rounds : []
          );
        } else {
          setEventName("Vertical Challenge");
          setEventStatus("completed");
          setRounds([]);
        }
      },
      (err) => {
        console.error("Error listening to event data:", err.message);
        setEventName("Vertical Challenge");
        setEventStatus("completed");
        setRounds([]);
      }
    );

    return () => unsubscribeEvent();
  }, [selectedEventId, db]);

  // Real-time listener for results
  useEffect(() => {
    const resultsQuery = query(collection(db, "resultVerticalChallenge"));

    const unsubscribeResults = onSnapshot(
      resultsQuery,
      (snapshot) => {
        const newResults = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              eventId: data.eventId || "",
              bib: data.bib || "",
              name: typeof data.name === "string" ? data.name : "N/A",
              gender: data.gender || null,
              rounds: data.rounds || {},
              lastRound: data.lastRound || null,
              genderRank: data.genderRank || null,
              totalElapsedTime: calculateTotalElapsedTime(
                data.rounds || {},
                rounds
              ),
              totalRemainingTime: formatCumulativeRemainingTime(
                data.rounds || {},
                rounds
              ),
            };
          })
          .filter((runner) => runner.eventId === selectedEventId);
        setResults(newResults);
      },
      (err) => {
        console.error("Error listening to results:", err.message);
        setResults([]);
      }
    );

    return () => unsubscribeResults();
  }, [selectedEventId, rounds]);

  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleTimeString("en-GB", {
      timeZone: "Asia/Jakarta",
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }, []);

  const formatElapsedTime = useCallback((elapsedTime) => {
    return elapsedTime && elapsedTime !== "N/A" ? elapsedTime : "-";
  }, []);

  const formatRemainingTime = useCallback((arrivalTime, cutOffTime) => {
    if (!arrivalTime || !cutOffTime) return "-";
    const arrival = moment(arrivalTime);
    const cot = moment(cutOffTime);
    if (!arrival.isValid() || !cot.isValid()) return "-";
    const diffMs = cot.diff(arrival);
    const duration = moment.duration(Math.abs(diffMs));
    const hours = Math.floor(duration.asHours()).toString().padStart(2, "0");
    const minutes = duration.minutes().toString().padStart(2, "0");
    const seconds = duration.seconds().toString().padStart(2, "0");
    const formattedTime = `${hours}:${minutes}:${seconds}`;
    return diffMs >= 0 ? formattedTime : `-${formattedTime}`;
  }, []);

  const calculateTotalElapsedTime = useCallback((roundsData, eventRounds) => {
    if (!roundsData || !eventRounds || !eventRounds.length) return "N/A";
    let totalMs = 0;
    let hasValidTime = false;
    eventRounds.forEach((round) => {
      if (roundsData[round.id]?.elapsedTime) {
        const [hours, minutes, seconds] = roundsData[round.id].elapsedTime
          .split(":")
          .map(Number);
        if (!isNaN(hours) && !isNaN(minutes) && !isNaN(seconds)) {
          totalMs += (hours * 3600 + minutes * 60 + seconds) * 1000;
          hasValidTime = true;
        }
      }
    });
    if (!hasValidTime) return "N/A";
    const duration = moment.duration(totalMs);
    return `${Math.floor(duration.asHours())
      .toString()
      .padStart(2, "0")}:${duration
      .minutes()
      .toString()
      .padStart(2, "0")}:${duration.seconds().toString().padStart(2, "0")}`;
  }, []);

  const formatCumulativeRemainingTime = useCallback(
    (roundsData, eventRounds) => {
      if (!roundsData || !eventRounds || !eventRounds.length) return "-";
      for (const round of eventRounds) {
        if (roundsData[round.id]?.isLate) {
          return "OVERCOT";
        }
      }
      let totalMs = 0;
      let hasValidTime = false;
      eventRounds.forEach((round) => {
        if (roundsData[round.id]?.time && round.cot) {
          const cotTime = moment(round.cot);
          const arrivalTime = moment(roundsData[round.id].time);
          const diffMs = cotTime.diff(arrivalTime);
          totalMs += diffMs;
          hasValidTime = true;
        }
      });
      if (!hasValidTime) return "-";
      const duration = moment.duration(Math.abs(totalMs));
      const hours = Math.floor(duration.asHours()).toString().padStart(2, "0");
      const minutes = duration.minutes().toString().padStart(2, "0");
      const seconds = duration.seconds().toString().padStart(2, "0");
      const formattedTime = `${hours}:${minutes}:${seconds}`;
      return totalMs >= 0 ? formattedTime : `-${formattedTime}`;
    },
    []
  );

  const getCumulativeRemainingTimeMs = useCallback((runner, rounds) => {
    if (!runner || !runner.rounds || !rounds.length) return -Infinity;
    for (const round of rounds) {
      if (runner.rounds[round.id]?.isLate) return -Infinity;
    }
    let totalMs = 0;
    rounds.forEach((round) => {
      if (runner.rounds[round.id]?.time && round.cot) {
        const cotTime = moment(round.cot);
        const arrivalTime = moment(runner.rounds[round.id].time);
        const diffMs = cotTime.diff(arrivalTime);
        totalMs += diffMs;
      }
    });
    return totalMs;
  }, []);

  const getCompletedRoundsCount = useCallback((runner, rounds) => {
    if (!runner || !runner.rounds || !rounds.length) return 0;
    return rounds.filter((round) => !!runner.rounds[round.id]?.elapsedTime)
      .length;
  }, []);

  const tableRounds = useMemo(() => {
    return rounds
      .filter((round) => round.id && round.name)
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [rounds]);

  const handleInputChange = useCallback((event) => {
    const input = event.target.value;
    setBibNumberInput(input);
    setIsTyping(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(input);
      setIsTyping(false);
    }, 500);
  }, []);

  const formatResults = useCallback(
    (runner) => {
      if (!runner.rounds) return tableRounds.map(() => "-");
      let isOvercot = false;
      return tableRounds.map((round) => {
        if (isOvercot) return "-";
        const roundData = runner.rounds[round.id];
        if (!roundData || !roundData.time) return "-";
        if (roundData.isLate) isOvercot = true;
        const elapsed = formatElapsedTime(roundData.elapsedTime);
        const remaining = formatRemainingTime(roundData.time, round.cot);
        return (
          <span
            key={round.id}
            className={
              roundData.isLate || remaining.startsWith("-")
                ? "text-red-500"
                : ""
            }
          >
            {elapsed} ({remaining})
          </span>
        );
      });
    },
    [tableRounds, formatElapsedTime, formatRemainingTime]
  );

  const filteredResults = useMemo(() => {
    return results.filter((runner) => {
      const search = searchQuery.toLowerCase();
      const matchesBIB =
        searchQuery && runner.bib
          ? runner.bib.toLowerCase().includes(search)
          : !searchQuery;
      const matchesName =
        searchQuery && typeof runner.name === "string"
          ? runner.name.toLowerCase().includes(search)
          : !searchQuery;
      return matchesBIB || matchesName;
    });
  }, [results, searchQuery]);

  const sortedResults = useMemo(() => {
    const round5 = tableRounds.find((round) => round.id === "round5");
    if (!round5) return filteredResults;

    const withRoundCount = filteredResults.map((runner) => ({
      ...runner,
      completedRounds: getCompletedRoundsCount(runner, tableRounds),
    }));

    const sorted = withRoundCount.sort((a, b) => {
      if (a.completedRounds !== b.completedRounds) {
        return b.completedRounds - a.completedRounds;
      }
      const isAFinisher = a.completedRounds === 5;
      const isBFinisher = b.completedRounds === 5;
      if (isAFinisher && isBFinisher) {
        const aTimeMs = getCumulativeRemainingTimeMs(a, rounds);
        const bTimeMs = getCumulativeRemainingTimeMs(b, rounds);
        return bTimeMs - aTimeMs;
      } else if (!isAFinisher && !isBFinisher) {
        const aName = typeof a.name === "string" ? a.name.toLowerCase() : "";
        const bName = typeof b.name === "string" ? b.name.toLowerCase() : "";
        return aName.localeCompare(bName);
      }
      return isAFinisher ? -1 : 1;
    });

    let maleRank = 1;
    let femaleRank = 1;
    let overallRank = 1;
    return sorted.map((runner) => {
      let genderRank = null;
      let rank = null;
      if (runner.completedRounds === 5) {
        rank = overallRank++;
        if (runner.gender === "male") {
          genderRank = maleRank++;
        } else if (runner.gender === "female") {
          genderRank = femaleRank++;
        }
      }
      return { ...runner, genderRank, overallRank: rank };
    });
  }, [
    filteredResults,
    rounds,
    getCompletedRoundsCount,
    getCumulativeRemainingTimeMs,
  ]);

  const isHighlighted = useCallback(
    (runner) => {
      if (!searchQuery) return false;
      const search = searchQuery.toLowerCase();
      const isBibMatch =
        runner.bib &&
        typeof runner.bib === "string" &&
        runner.bib.toLowerCase() === search;
      const isNameMatch =
        typeof runner.name === "string" &&
        search.length >= 3 &&
        runner.name.toLowerCase().includes(search);
      return isBibMatch || isNameMatch;
    },
    [searchQuery]
  );

  const isFinisher = useCallback(
    (runner) => {
      const round5 = tableRounds.find((round) => round.id === "round5");
      return !!round5 && !!runner.rounds[round5.id]?.elapsedTime;
    },
    [tableRounds]
  );

  const generateCertificate = useCallback(
    (runner) => {
      if (!runner || !runner.name || !runner.bib) {
        alert("Invalid runner data.");
        return;
      }

      const round5 = tableRounds.find((round) => round.id === "round5");
      if (!round5 || !runner.rounds[round5.id]?.elapsedTime) {
        alert(`${runner.name} is not a finisher (no valid time for Round 5).`);
        return;
      }

      const canvas = canvasRef.current;
      if (!canvas) {
        alert("Canvas not available.");
        return;
      }

      const ctx = canvas.getContext("2d");
      canvas.width = 800; // 4:5 aspect ratio
      canvas.height = 1000;

      const templateImage = new Image();
      templateImage.src = "/sertifikat.png";

      templateImage.onload = () => {
        ctx.drawImage(templateImage, 0, 0, canvas.width, canvas.height);

        ctx.fillStyle = "#000000";

        // spacing
        const lineHeight = 36; // jarak normal antar elemen
        const gapNama = 70;
        const gapRank = 40;
        const gapBib = 40;
        const gapAfterBib = 100; // jarak ekstra sebelum rounds
        const roundLineHeight = 50; // jarak antar label rounds

        // posisi horizontal (dua kolom untuk round)
        const startXLeft = 240;
        const startXRight = 460;

        // ===== offset ke bawah agar konten lebih naik =====
        const baseY = 300;

        // ===== Nama =====
        ctx.textAlign = "center";
        ctx.font = "bold 64px 'Oxanium', sans-serif";
        ctx.fillText(runner.name.toUpperCase(), canvas.width / 2, baseY);

        let yPosition = baseY + gapNama;

        // ===== Total Time (Elapsed and Remaining) =====
        ctx.font = "36px 'Oxanium', sans-serif";
        ctx.fillStyle =
          runner.totalRemainingTime === "OVERCOT" ||
          runner.totalRemainingTime.startsWith("-")
            ? "#FF0000"
            : "#000000";
        ctx.fillText(
          `${runner.totalElapsedTime || "-"} (${
            runner.totalRemainingTime || "-"
          })`,
          canvas.width / 2,
          yPosition
        );

        yPosition += lineHeight;

        // ===== Rank =====
        const rankText = `Rank ${runner.genderRank || "N/A"} ${(
          runner.gender || ""
        ).toUpperCase()} | ${runner.overallRank || "N/A"} All`;

        ctx.fillStyle = "#000000";
        ctx.font = "30px 'Oxanium', sans-serif";
        ctx.fillText(rankText, canvas.width / 2, yPosition);

        yPosition += gapRank;

        // ===== BIB =====
        ctx.font = "28px 'Oxanium', sans-serif";
        ctx.fillText(`BIB: ${runner.bib}`, canvas.width / 2, yPosition);

        // kasih jarak ekstra sebelum rounds
        yPosition += gapAfterBib;

        // ===== Rounds =====
        ctx.font = "28px 'Oxanium', sans-serif";
        ctx.textAlign = "left";

        tableRounds.forEach((round) => {
          const roundData = runner.rounds[round.id];
          const elapsed = roundData?.elapsedTime
            ? formatElapsedTime(roundData.elapsedTime)
            : "-";

          // label round putih
          ctx.fillStyle = "#FFFFFF";
          ctx.fillText(round.name + ":", startXLeft, yPosition);

          // value hitam
          ctx.fillStyle = "#000000";
          ctx.fillText(elapsed, startXRight, yPosition);

          yPosition += roundLineHeight; // jarak antar round lebih longgar
        });

        // Download otomatis
        const link = document.createElement("a");
        link.download = `${runner.name || "Unknown"}-Certificate.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
      };

      templateImage.onerror = () => {
        alert("Failed to load certificate template image.");
      };
    },
    [tableRounds, formatElapsedTime]
  );

  return (
    <main>
      <canvas ref={canvasRef} style={{ display: "none" }} />
      <div className="min-h-screen bg-yellow-50 flex flex-col py-20">
        <div className="text-center mb-8">
          <h2 className="text-sm md:text-3xl font-oxanium font-semibold text-yellow-600 uppercase">
            Official Race Result
          </h2>
          <p className="text-3xl md:text-6xl font-oxanium font-bold text-yellow-600 mt-2">
            {eventName.toUpperCase()}
          </p>
        </div>
        <div className="flex flex-col md:flex-row md:justify-between md:items-center px-4 mb-4">
          <h1 className="text-xl md:text-2xl text-center uppercase md:text-right font-extrabold text-yellow-600 tracking-tight font-oxanium">
            Disiksa Abah Donny
          </h1>
          <div className="flex items-center text-yellow-600 justify-center md:justify-start mt-4 md:mt-0">
            <input
              type="text"
              value={bibNumberInput}
              onChange={handleInputChange}
              placeholder="Search BIB or Name"
              className="border border-yellow-200 p-2 pl-4 rounded-xl w-full md:w-64 text-yellow-600 text-center md:text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
          </div>
        </div>
        <div className="overflow-x-auto px-4">
          <div className="bg-white border border-yellow-400 mb-4 rounded-xl w-full overflow-x-auto">
            <table className="min-w-full table-auto font-oxanium">
              <thead>
                <tr className="bg-yellow-600">
                  {[
                    { header: "NO", colspan: 1 },
                    { header: "BIB", colspan: 1 },
                    { header: "NAME", colspan: 1 },
                    ...tableRounds.map((round) => ({
                      header: round.name.toUpperCase(),
                      colspan: 1,
                    })),
                    { header: "TOTAL TIME", colspan: 1 },
                    { header: "CERTIFICATE", colspan: 1 },
                  ].map((item, idx, arr) => (
                    <th
                      key={item.header}
                      colSpan={item.colspan}
                      className={`px-4 py-2 text-base font-bold text-white text-center whitespace-nowrap ${
                        idx === 0 ? "rounded-tl-xl" : ""
                      } ${idx === arr.length - 1 ? "rounded-tr-xl" : ""}`}
                    >
                      {item.header}
                    </th>
                  ))}
                </tr>
                <tr className="bg-yellow-300">
                  {[
                    { header: "", colspan: 1 },
                    { header: "", colspan: 1 },
                    { header: "", colspan: 1 },
                    ...tableRounds.map(() => ({
                      header: "Elapsed (Remaining)",
                      colspan: 1,
                    })),
                    { header: "Elapsed (Remaining)", colspan: 1 },
                    { header: "", colspan: 1 },
                  ].map((item, idx) => (
                    <th
                      key={idx}
                      colSpan={item.colspan}
                      className="px-4 py-2 text-sm font-semibold text-yellow-800 text-center whitespace-nowrap"
                    >
                      {item.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedResults.length > 0 ? (
                  sortedResults.map((runner, index) => {
                    const isLast = index === sortedResults.length - 1;
                    const roundData = formatResults(runner);
                    return (
                      <tr
                        key={runner.bib}
                        className={isHighlighted(runner) ? "bg-yellow-200" : ""}
                      >
                        <td
                          className={`px-4 py-2 text-center text-sm whitespace-nowrap ${
                            !isLast ? "border-b border-yellow-100" : ""
                          }`}
                        >
                          {runner.genderRank || "-"}
                        </td>
                        <td
                          className={`px-4 py-2 text-center text-sm whitespace-nowrap ${
                            !isLast ? "border-b border-yellow-100" : ""
                          }`}
                        >
                          {runner.bib}
                        </td>
                        <td
                          className={`px-4 py-2 text-sm whitespace-nowrap ${
                            !isLast ? "border-b border-yellow-100" : ""
                          }`}
                        >
                          {typeof runner.name === "string"
                            ? runner.name.toUpperCase()
                            : "N/A"}
                        </td>
                        {roundData.map((data, idx) => (
                          <td
                            key={`${runner.bib}-${idx}-elapsed`}
                            className={`px-4 py-2 text-center text-sm whitespace-nowrap ${
                              !isLast ? "border-b border-yellow-100" : ""
                            }`}
                          >
                            {data}
                          </td>
                        ))}
                        <td
                          className={`px-4 py-2 text-center text-sm whitespace-nowrap ${
                            !isLast ? "border-b border-yellow-100" : ""
                          } ${
                            runner.totalRemainingTime === "OVERCOT" ||
                            runner.totalRemainingTime.startsWith("-")
                              ? "text-red-500"
                              : ""
                          }`}
                        >
                          {runner.totalElapsedTime} ({runner.totalRemainingTime}
                          )
                        </td>
                        <td
                          className={`px-4 py-2 text-center text-sm whitespace-nowrap ${
                            !isLast ? "border-b border-yellow-100" : ""
                          }`}
                        >
                          {isFinisher(runner) ? (
                            <button
                              onClick={() => generateCertificate(runner)}
                              className="bg-yellow-600 text-white py-1 px-2 rounded hover:bg-yellow-700"
                            >
                              Download
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={4 + tableRounds.length}
                      className="px-4 py-2 border-b border-yellow-200 text-center text-sm text-yellow-600 rounded-b-xl whitespace-nowrap"
                    >
                      Tidak ada data peserta. Silakan periksa data di Firebase
                      atau unggah peserta di halaman Admin.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Results;
